/*
 * What goes back to the pharmacy.
 *
 * The home puts things in the bag and says why; the pharmacy signs for it when the driver takes
 * it. A controlled drug needs a second person watching and comes off the register the moment it
 * goes in the bag — not when it is collected — so the register still balances against the
 * cupboard that night.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Resident, User, Witness } from './api.ts';
import { Overlay } from './overlay.tsx';

type Item = {
  id: string;
  description: string;
  qty: number;
  unit: string;
  reason: string;
  reasonLabel: string;
  note: string;
  controlledDrug: boolean;
  residentName: string;
  addedBy: string;
  witness: string;
  addedAt: string;
};

type Batch = {
  id: string;
  status: 'open' | 'ready' | 'collected';
  note: string;
  createdAt: string;
  readyAt: string | null;
  collectedAt: string | null;
  collectedBy: string;
  signedBy: string;
  items: Item[];
};

type Medicine = { id: string; name: string; doseText: string; controlledDrug: boolean };

const STATUS: Record<Batch['status'], string> = {
  open: 'Being filled',
  ready: 'Ready for collection',
  collected: 'Collected',
};

export function ReturnsScreen({ homeId, user }: { homeId: string; user: User }) {
  const [batches, setBatches] = useState<Batch[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [signing, setSigning] = useState<Batch | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ batches: Batch[] }>(`/api/homes/${homeId}/returns`);
      setBatches(data.batches);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the returns.');
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isHome = user.side === 'home';
  const isPharmacy = user.roles.includes('pharmacy');

  const step = useCallback(
    async (batch: Batch, which: 'ready') => {
      try {
        await api.post(`/api/returns/${batch.id}/${which}`, {});
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      }
    },
    [load],
  );

  return (
    <section>
      <div className="head">
        <h1>Going back to the pharmacy</h1>
        <div className="row-actions">
          {isHome ? <button type="button" onClick={() => setAdding(true)}>Put something in the bag</button> : null}
          <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      <p className="muted">
        Anything stopped, out of date, damaged or left behind. A controlled drug needs a second person
        watching and comes off the register straight away.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {batches && !batches.length ? <p className="notice">Nothing waiting to go back.</p> : null}

      {(batches ?? []).map((batch) => (
        <article key={batch.id} className="card">
          <div className="head">
            <div>
              <h2>{STATUS[batch.status]}</h2>
              <p className="muted small">
                Started {formatWhen(batch.createdAt)}
                {batch.collectedAt
                  ? ` · collected ${formatWhen(batch.collectedAt)} by ${batch.collectedBy}${
                      batch.signedBy ? `, signed for by ${batch.signedBy}` : ''
                    }`
                  : batch.readyAt
                    ? ` · ready since ${formatWhen(batch.readyAt)}`
                    : ''}
              </p>
            </div>
            <div className="row-actions">
              {isHome && batch.status === 'open' && batch.items.length ? (
                <button type="button" onClick={() => void step(batch, 'ready')}>The bag is ready</button>
              ) : null}
              {isPharmacy && batch.status !== 'collected' ? (
                <button type="button" onClick={() => setSigning(batch)}>Record collecting it</button>
              ) : null}
            </div>
          </div>

          <div className="scroll">
            <table className="register">
              <thead>
                <tr><th>What</th><th className="num">How much</th><th>Why</th><th>Who</th></tr>
              </thead>
              <tbody>
                {batch.items.map((item) => (
                  <tr key={item.id} className={item.controlledDrug ? 'mismatch' : ''}>
                    <td>
                      {item.description}
                      {item.controlledDrug ? <small>Controlled drug · witnessed by {item.witness}</small> : null}
                      {item.residentName ? <small>{item.residentName}</small> : null}
                    </td>
                    <td className="num">{item.qty}{item.unit ? ` ${item.unit}` : ''}</td>
                    <td>
                      {item.reasonLabel}
                      {item.note ? <small>{item.note}</small> : null}
                    </td>
                    <td>{item.addedBy}<small>{formatWhen(item.addedAt)}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!batch.items.length ? <p className="muted small">Nothing in this bag yet.</p> : null}
        </article>
      ))}

      {adding ? (
        <AddDialog
          homeId={homeId}
          user={user}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            void load();
          }}
        />
      ) : null}

      {signing ? (
        <SignDialog
          batch={signing}
          onClose={() => setSigning(null)}
          onDone={() => {
            setSigning(null);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

function AddDialog({
  homeId,
  user,
  onClose,
  onDone,
}: {
  homeId: string;
  user: User;
  onClose: () => void;
  onDone: () => void;
}) {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [residentId, setResidentId] = useState('');
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [medicineId, setMedicineId] = useState('');
  const [description, setDescription] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('tablets');
  const [reason, setReason] = useState('stopped');
  const [note, setNote] = useState('');
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [witnessId, setWitnessId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ residents: Resident[] }>(`/api/homes/${homeId}/residents`)
      .then((data) => setResidents(data.residents))
      .catch(() => setResidents([]));
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses.filter((w) => w.id !== user.id)))
      .catch(() => setWitnesses([]));
  }, [homeId, user.id]);

  useEffect(() => {
    if (!residentId) {
      setMedicines([]);
      return;
    }
    api
      .get<{ medicines: Medicine[] }>(`/api/residents/${residentId}/medicines`)
      .then((data) => setMedicines(data.medicines))
      .catch(() => setMedicines([]));
  }, [residentId]);

  const chosen = medicines.find((medicine) => medicine.id === medicineId) ?? null;

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/homes/${homeId}/returns`, {
          description: description || chosen?.name || '',
          qty: Number(qty),
          unit,
          reason,
          note,
          residentId: residentId || null,
          medicineId: medicineId || null,
          witnessId: witnessId || null,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [homeId, description, chosen, qty, unit, reason, note, residentId, medicineId, witnessId, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Something to go back</h2>

        <label htmlFor="ret-resident">Whose is it?</label>
        <select id="ret-resident" value={residentId} onChange={(e) => { setResidentId(e.target.value); setMedicineId(''); }}>
          <option value="">Nobody's in particular — stock or out of date</option>
          {residents.map((resident) => (
            <option key={resident.id} value={resident.id}>
              {resident.title ? `${resident.title} ` : ''}{resident.firstName} {resident.lastName}
              {resident.room ? ` · Room ${resident.room}` : ''}
            </option>
          ))}
        </select>

        {residentId ? (
          <>
            <label htmlFor="ret-medicine">Which medicine?</label>
            <select
              id="ret-medicine"
              value={medicineId}
              onChange={(e) => {
                setMedicineId(e.target.value);
                const picked = medicines.find((medicine) => medicine.id === e.target.value);
                if (picked && !description) setDescription(picked.name);
              }}
            >
              <option value="">Not on their chart — type it below</option>
              {medicines.map((medicine) => (
                <option key={medicine.id} value={medicine.id}>
                  {medicine.name}{medicine.controlledDrug ? ' · controlled drug' : ''}
                </option>
              ))}
            </select>
          </>
        ) : null}

        <label htmlFor="ret-what">What is it, as written on the box?</label>
        <input id="ret-what" required minLength={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Amlodipine 5mg tablets, part pack" />

        <div className="slot-top">
          <div className="narrow">
            <label htmlFor="ret-qty">How much</label>
            <input id="ret-qty" type="number" step="0.5" min="0.5" required inputMode="decimal"
              value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div className="narrow">
            <label htmlFor="ret-unit">Of what</label>
            <input id="ret-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="tablets" />
          </div>
          <div>
            <label htmlFor="ret-reason">Why is it going back?</label>
            <select id="ret-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="stopped">Stopped</option>
              <option value="refused">Refused and cannot be reused</option>
              <option value="expired">Out of date</option>
              <option value="excess">Too much sent</option>
              <option value="damaged">Damaged</option>
              <option value="left">Resident has left or died</option>
            </select>
          </div>
        </div>

        <label htmlFor="ret-note">Anything else worth knowing</label>
        <input id="ret-note" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Stopped by the GP on Monday" />

        {chosen?.controlledDrug ? (
          <fieldset>
            <legend>Controlled drug</legend>
            <p className="muted small">
              Count what is left with a second person before it goes in the bag. It comes off the register now,
              so the register and the cupboard match at the next check.
            </p>
            <label htmlFor="ret-witness">Who watched you count it?</label>
            <select id="ret-witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
              <option value="">Choose a person</option>
              {witnesses.map((witness) => (
                <option key={witness.id} value={witness.id}>{witness.name} · {witness.jobTitle}</option>
              ))}
            </select>
          </fieldset>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Put it in the bag'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function SignDialog({ batch, onClose, onDone }: { batch: Batch; onClose: () => void; onDone: () => void }) {
  const [signedBy, setSignedBy] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/returns/${batch.id}/collected`, { signedBy });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [batch.id, signedBy, onDone],
  );

  const controlled = batch.items.filter((item) => item.controlledDrug);

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Collected from the home</h2>
        <p className="muted">
          {batch.items.length} item{batch.items.length === 1 ? '' : 's'}
          {controlled.length ? `, ${controlled.length} of them controlled drugs` : ''}.
        </p>

        {controlled.length ? (
          <ul className="list">
            {controlled.map((item) => (
              <li key={item.id}>
                <div className="row static">
                  <span className="row-main">{item.description}</span>
                  <span className="muted small">{item.qty}{item.unit ? ` ${item.unit}` : ''}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        <label htmlFor="sign-by">Who took it away?</label>
        <input id="sign-by" required minLength={2} value={signedBy} onChange={(e) => setSignedBy(e.target.value)}
          placeholder="The driver's name" />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record it as collected'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)} ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
